// src/components/dashboard/widgets/nearby-events-widget.tsx
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

interface EventSummary { name: string; date: string; venue: string; distanceKm: number; major: boolean }

interface NearbyEventsWidgetProps {
  lat: number | null;
  lon: number | null;
}

function formatDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function NearbyEventsWidget({ lat, lon }: NearbyEventsWidgetProps) {
  const c = useAppColors();
  const [events, setEvents] = useState<EventSummary[] | undefined>(undefined);

  useEffect(() => {
    if (lat == null || lon == null) { setEvents([]); return; }
    supabase.functions.invoke('nearby-events', { body: { lat, lon } }).then(({ data }) => {
      setEvents(data?.ok ? (data.events as EventSummary[]) : []);
    });
  }, [lat, lon]);

  const hasMajor = events && events.some((e) => e.major);

  return (
    <div
      className="rounded-2xl border p-4 h-full"
      style={hasMajor
        ? { background: 'rgba(232,116,106,0.07)', borderColor: 'rgba(232,116,106,0.35)' }
        : { borderColor: 'rgba(74,154,74,0.2)' }
      }
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={hasMajor ? { background: 'rgba(232,160,32,0.15)' } : { background: 'rgba(74,154,74,0.1)' }}>
          <Sparkles size={15} style={hasMajor ? { color: '#E8A020' } : undefined} className={hasMajor ? '' : 'text-leaf-400'} />
        </div>
        <span className="text-[9px] font-bold tracking-widest uppercase" style={{ color: hasMajor ? '#E8A020' : 'rgba(245,245,242,0.5)' }}>
          Nearby events
        </span>
      </div>
      {events === undefined ? (
        <div className="text-xs text-cream-muted/50">Loading…</div>
      ) : lat == null ? (
        <div className="text-xs text-cream-muted/50">Set your venue address to see nearby events</div>
      ) : events.length === 0 ? (
        <div className="text-xs text-cream-muted">Nothing major nearby in the next 2 weeks</div>
      ) : (
        <div className="space-y-2">
          {events.slice(0, 3).map((e, i) => (
            <div key={i} className="flex items-start justify-between gap-2">
              <span className="text-[11px] text-cream leading-snug truncate">{e.name}{e.major && ' ⭐'}</span>
              <span className="text-[10px] text-cream-muted flex-shrink-0">{formatDate(e.date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}